import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import BookAppointmentLink from '../components/BookAppointmentLink';

const NotFound: React.FC = () => {
  const [dogPosition, setDogPosition] = useState({ x: 50, y: 50 });
  const [isSniffing, setIsSniffing] = useState(false);

  // Make the dog wander around looking for the page
  useEffect(() => {
    const interval = setInterval(() => {
      setDogPosition({
        x: 20 + Math.random() * 60,
        y: 30 + Math.random() * 40,
      });
      setIsSniffing(Math.random() > 0.5);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const funMessages = [
    "The page ran away to chase squirrels! 🐿️",
    "This page needs a dental cleaning... it's nowhere to be found! 🦷",
    "Even our best sniffers can't find this page! 🐕",
    "404: Page is at the dog park having too much fun! 🎾",
    "This page forgot where it buried itself! 🦴",
  ];

  const [message] = useState(funMessages[Math.floor(Math.random() * funMessages.length)]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 relative overflow-hidden">
      {/* Floating elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 15 }).map((_, i) => (
          <div
            key={i}
            className="absolute text-3xl animate-float-slow"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${8 + Math.random() * 12}s`,
            }}
          >
            {['🦴', '🦷', '🐾', '❓', '🔍'][Math.floor(Math.random() * 5)]}
          </div>
        ))}
      </div>

      <div className="flex-grow flex items-center justify-center px-4 relative z-10">
        <div className="text-center max-w-3xl">
          {/* Animated searching dog */}
          <div className="relative mb-8 h-64">
            <div
              className="absolute transition-all duration-3000 ease-in-out"
              style={{
                left: `${dogPosition.x}%`,
                top: `${dogPosition.y}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <div className={`text-8xl ${isSniffing ? 'animate-sniff' : 'animate-bounce-gentle'}`}>
                🐕
              </div>
              {isSniffing && (
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 text-2xl animate-fade-in-out">
                  👃
                </div>
              )}
            </div>
            
            {/* Paw prints trail */}
            <div className="absolute inset-0 pointer-events-none">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute text-2xl opacity-30 animate-fade-in-out"
                  style={{
                    left: `${30 + i * 6}%`,
                    top: `${40 + (i % 2 ? 5 : -5)}%`,
                    animationDelay: `${i * 0.3}s`,
                  }}
                >
                  🐾
                </div>
              ))}
            </div>
          </div>

          {/* Error message */}
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 md:p-12 border-2 border-purple-200">
            <h1 className="text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-500 mb-4">
              404
            </h1>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Uh-Oh! Page Not Found
            </h2>
            <p className="text-xl text-gray-600 mb-4">
              {message}
            </p>
            <p className="text-lg text-gray-500 mb-8">
              Don't worry though - our dogs never give up the search! 
              Let's get you back on the right path.
            </p>

            {/* Fun stats */}
            <div className="grid grid-cols-3 gap-4 mb-8 text-center">
              <div className="bg-gradient-to-br from-pink-100 to-pink-200 rounded-xl p-4">
                <div className="text-3xl mb-2">🔍</div>
                <div className="text-sm font-semibold text-gray-700">Still Searching...</div>
              </div>
              <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl p-4">
                <div className="text-3xl mb-2">🐾</div>
                <div className="text-sm font-semibold text-gray-700">Tracks Followed: ∞</div>
              </div>
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-4">
                <div className="text-3xl mb-2">🦴</div>
                <div className="text-sm font-semibold text-gray-700">Bones Found: 0</div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Link
                to="/"
                className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-8 py-4 rounded-full font-bold hover:scale-105 transition-transform shadow-lg inline-flex items-center justify-center"
              >
                <i className="fas fa-home mr-2"></i>
                Take Me Home
              </Link>
              <Link
                to="/services"
                className="bg-gradient-to-r from-blue-500 to-teal-500 text-white px-8 py-4 rounded-full font-bold hover:scale-105 transition-transform shadow-lg inline-flex items-center justify-center"
              >
                <i className="fas fa-tooth mr-2"></i>
                View Services
              </Link>
            </div>

            {/* Helpful links */}
            <div className="pt-6 border-t border-gray-200">
              <p className="text-gray-600 mb-4 font-semibold">Quick Links (No Digging Required!):</p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <Link to="/team" className="text-brand-teal hover:underline font-medium">
                  👥 Our Team
                </Link>
                <Link to="/contact" className="text-brand-teal hover:underline font-medium">
                  📞 Contact Us
                </Link>
                <BookAppointmentLink className="text-brand-teal hover:underline font-medium">
                  📅 Book Appointment
                </BookAppointmentLink>
                <Link to="/research" className="text-brand-teal hover:underline font-medium">
                  🔬 Research
                </Link>
              </div>
            </div>

            {/* Easter egg hint */}
            <div className="mt-8 text-xs text-gray-400 italic">
              💡 Pro tip: Some pages are hiding better than others... like on the team page 👀
            </div>
          </div>
        </div>
      </div>

      <Footer />

      <style>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.3; }
          50% { transform: translateY(-30px) rotate(180deg); opacity: 0.6; }
        }

        @keyframes sniff {
          0%, 100% { transform: scale(1) translateY(0); }
          25% { transform: scale(1.1) translateY(-5px); }
          50% { transform: scale(0.9) translateY(2px); }
          75% { transform: scale(1.05) translateY(-2px); }
        }

        @keyframes bounce-gentle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }

        @keyframes fade-in-out {
          0%, 100% { opacity: 0; }
          50% { opacity: 0.5; }
        }

        .animate-float-slow {
          animation: float-slow ease-in-out infinite;
        }

        .animate-sniff {
          animation: sniff 0.6s ease-in-out infinite;
        }

        .animate-bounce-gentle {
          animation: bounce-gentle 2s ease-in-out infinite;
        }

        .animate-fade-in-out {
          animation: fade-in-out 2s ease-in-out infinite;
        }

        .duration-3000 {
          transition-duration: 3000ms;
        }
      `}</style>
    </div>
  );
};

export default NotFound;
